import { useState, useEffect } from 'react';
import { Package, Truck, FileCheck, Building2, CreditCard, DollarSign, User, Check, Calendar, Save, Loader2, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('mosaic_session_token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const STAGES = [
  { key: 'shipped', label: 'Shipped', icon: Truck, color: 'blue' },
  { key: 'proof_of_delivery', label: 'Proof of Delivery', icon: FileCheck, color: 'cyan' },
  { key: 'received_by_school', label: 'Received by School', icon: Package, color: 'emerald' },
  { key: 'billed_to_nyc', label: 'Billed to NYC', icon: Building2, color: 'amber' },
  { key: 'payment_received', label: 'Payment Received', icon: CreditCard, color: 'green' },
];

const FulfillmentTracker = ({ quote, onUpdate }) => {
  const f = quote.fulfillment || {};
  const [form, setForm] = useState({
    shipped: f.shipped || false,
    shipped_date: f.shipped_date || '',
    tracking_number: f.tracking_number || '',
    carrier: f.carrier || '',
    proof_of_delivery: f.proof_of_delivery || false,
    pod_date: f.pod_date || '',
    pod_reference: f.pod_reference || '',
    received_by_school: f.received_by_school || false,
    received_date: f.received_date || '',
    billed_to_nyc: f.billed_to_nyc || false,
    invoice_number: f.invoice_number || '',
    invoice_date: f.invoice_date || '',
    invoice_amount: f.invoice_amount || quote.subtotal || 0,
    payment_received: f.payment_received || false,
    payment_date: f.payment_date || '',
    payment_amount: f.payment_amount || 0,
    payment_reference: f.payment_reference || '',
    sales_rep: f.sales_rep || '',
    commission_pct: f.commission_pct || 10,
    commission_amount: f.commission_amount || 0,
    commission_amount_manual: f.commission_amount_manual || false,
    commission_paid: f.commission_paid || false,
    commission_paid_date: f.commission_paid_date || '',
    notes: f.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reps, setReps] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const hasDigitalItems = (quote.items || []).some(
    item => (item.sku || '').includes('DIG') || (item.sku || '').includes('BDL')
  );
  const welcomeEmailSent = quote.welcome_email_sent;

  useEffect(() => {
    axios.get(`${API}/commissions/reps`, authHeader())
      .then(res => setReps(res.data.reps || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Only auto-recompute the commission amount when it has NOT been
    // manually overridden. This lets a user set commission_pct to 0
    // (or any custom value) and enter an arbitrary commission_amount
    // without the form clobbering it on every render.
    if (form.commission_amount_manual) return;
    const amt = Math.round((quote.subtotal || 0) * (form.commission_pct / 100) * 100) / 100;
    if (amt !== form.commission_amount) {
      setForm(prev => ({ ...prev, commission_amount: amt }));
      setDirty(true);
    }
  }, [form.commission_pct, quote.subtotal, form.commission_amount_manual]);

  const update = (key, val) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/quotes/${quote.quote_number}/fulfillment`, form, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (onUpdate) onUpdate(quote.quote_number, res.data.fulfillment);
      setDirty(false);
    } catch (err) {
      console.error('Failed to save fulfillment:', err);
    } finally {
      setSaving(false);
    }
  };

  const sendWelcomeEmail = async () => {
    if (!window.confirm(`Send Welcome to MOSAIC email to ${quote.contact?.contact_email || 'the contact'}?`)) return;
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/quotes/${quote.quote_number}/send-welcome-email`, {}, authHeader());
      setEmailResult(res.data);
      if (res.data.email_delivered) {
        toast.success('Welcome email sent successfully!');
      } else {
        toast.warning('Admin credentials generated. Email delivery failed — check SendGrid configuration.');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to send email';
      toast.error(msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const completedStages = STAGES.filter(s => form[s.key]).length;

  return (
    <div className="mt-3 border-t border-purple-200 pt-3" data-testid="fulfillment-tracker">
      {/* Progress Bar */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold text-purple-800">Order Fulfillment Pipeline</h4>
        <span className="text-[10px] text-purple-500 font-medium">{completedStages}/{STAGES.length} stages complete</span>
      </div>

      <div className="flex items-center gap-0.5 mb-4">
        {STAGES.map((s, i) => {
          const done = form[s.key];
          const Icon = s.icon;
          return (
            <div key={s.key} className="flex items-center flex-1">
              <button
                onClick={() => update(s.key, !done)}
                className={`flex items-center gap-1 px-2 py-1.5 rounded text-[9px] font-bold w-full transition-all ${
                  done ? `bg-${s.color}-100 text-${s.color}-700 border border-${s.color}-300` : 'bg-gray-50 text-gray-400 border border-gray-200 hover:bg-gray-100'
                }`}
                data-testid={`stage-${s.key}`}
              >
                {done ? <Check size={10} className="flex-shrink-0" /> : <Icon size={10} className="flex-shrink-0" />}
                <span className="truncate">{s.label}</span>
              </button>
              {i < STAGES.length - 1 && <div className={`w-2 h-0.5 mx-0.5 flex-shrink-0 ${done ? 'bg-emerald-400' : 'bg-gray-200'}`} />}
            </div>
          );
        })}
      </div>

      {/* Detail Fields */}
      <div className="grid grid-cols-3 gap-3 text-[10px]">
        {/* Shipping */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><Truck size={10} /> Shipping</p>
          <div>
            <label className="text-gray-500">Ship Date</label>
            <Input type="date" value={form.shipped_date} onChange={e => update('shipped_date', e.target.value)} className="h-6 text-[10px]" data-testid="input-shipped-date" />
          </div>
          <div>
            <label className="text-gray-500">Tracking #</label>
            <Input value={form.tracking_number} onChange={e => update('tracking_number', e.target.value)} placeholder="e.g., 1Z999..." className="h-6 text-[10px]" data-testid="input-tracking" />
          </div>
          <div>
            <label className="text-gray-500">Carrier</label>
            <Input value={form.carrier} onChange={e => update('carrier', e.target.value)} placeholder="UPS, FedEx..." className="h-6 text-[10px]" data-testid="input-carrier" />
          </div>
        </div>

        {/* Delivery & Receipt */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><FileCheck size={10} /> Delivery & Receipt</p>
          <div>
            <label className="text-gray-500">POD Date</label>
            <Input type="date" value={form.pod_date} onChange={e => update('pod_date', e.target.value)} className="h-6 text-[10px]" data-testid="input-pod-date" />
          </div>
          <div>
            <label className="text-gray-500">POD Reference</label>
            <Input value={form.pod_reference} onChange={e => update('pod_reference', e.target.value)} placeholder="Delivery confirmation #" className="h-6 text-[10px]" data-testid="input-pod-ref" />
          </div>
          <div>
            <label className="text-gray-500">Received Date</label>
            <Input type="date" value={form.received_date} onChange={e => update('received_date', e.target.value)} className="h-6 text-[10px]" data-testid="input-received-date" />
          </div>
        </div>

        {/* Billing */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><Building2 size={10} /> NYC Billing</p>
          <div>
            <label className="text-gray-500">Invoice #</label>
            <Input value={form.invoice_number} onChange={e => update('invoice_number', e.target.value)} placeholder="INV-2026-..." className="h-6 text-[10px]" data-testid="input-invoice-num" />
          </div>
          <div>
            <label className="text-gray-500">Invoice Date</label>
            <Input type="date" value={form.invoice_date} onChange={e => update('invoice_date', e.target.value)} className="h-6 text-[10px]" data-testid="input-invoice-date" />
          </div>
          <div>
            <label className="text-gray-500">Invoice Amount</label>
            <Input type="number" step="0.01" value={form.invoice_amount} onChange={e => update('invoice_amount', parseFloat(e.target.value) || 0)} className="h-6 text-[10px]" data-testid="input-invoice-amt" />
          </div>
        </div>

        {/* Payment */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><CreditCard size={10} /> Payment</p>
          <div>
            <label className="text-gray-500">Payment Date</label>
            <Input type="date" value={form.payment_date} onChange={e => update('payment_date', e.target.value)} className="h-6 text-[10px]" data-testid="input-payment-date" />
          </div>
          <div>
            <label className="text-gray-500">Amount Received</label>
            <Input type="number" step="0.01" value={form.payment_amount} onChange={e => update('payment_amount', parseFloat(e.target.value) || 0)} className="h-6 text-[10px]" data-testid="input-payment-amt" />
          </div>
          <div>
            <label className="text-gray-500">Reference / Check #</label>
            <Input value={form.payment_reference} onChange={e => update('payment_reference', e.target.value)} placeholder="PO# or check #" className="h-6 text-[10px]" data-testid="input-payment-ref" />
          </div>
        </div>

        {/* Commission */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><User size={10} /> Sales Rep & Commission</p>
          <div>
            <label className="text-gray-500">Sales Rep</label>
            <select
              value={form.sales_rep}
              onChange={e => update('sales_rep', e.target.value)}
              className="w-full h-6 text-[10px] border border-gray-200 rounded px-2 bg-white"
              data-testid="input-sales-rep"
            >
              <option value="">Select Rep...</option>
              {reps.map(r => (
                <option key={r.rep_id || r.name} value={r.name}>{r.name} — {r.territory}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-1">
            <div>
              <label className="text-gray-500">Commission %</label>
              <Input type="number" step="0.5" min="0" max="100" value={form.commission_pct} onChange={e => update('commission_pct', parseFloat(e.target.value) || 0)} className="h-6 text-[10px]" data-testid="input-commission-pct" />
            </div>
            <div>
              <label className="text-gray-500 flex items-center justify-between gap-1">
                <span>Amount Due</span>
                {form.commission_amount_manual && (
                  <button
                    type="button"
                    onClick={() => {
                      const amt = Math.round((quote.subtotal || 0) * (form.commission_pct / 100) * 100) / 100;
                      setForm(prev => ({ ...prev, commission_amount_manual: false, commission_amount: amt }));
                      setDirty(true);
                    }}
                    className="text-[9px] text-blue-600 hover:underline font-medium"
                    data-testid="btn-commission-reset-auto"
                    title="Recalculate from Commission %"
                  >
                    Reset to auto
                  </button>
                )}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.commission_amount}
                  onChange={e => {
                    const v = parseFloat(e.target.value);
                    setForm(prev => ({
                      ...prev,
                      commission_amount: isNaN(v) ? 0 : v,
                      commission_amount_manual: true,
                    }));
                    setDirty(true);
                  }}
                  className={`h-6 text-[10px] font-bold pr-14 ${form.commission_amount_manual ? 'text-amber-800 bg-amber-50 border-amber-300' : 'text-emerald-700 bg-emerald-50 border-emerald-200'}`}
                  data-testid="input-commission-amount"
                />
                {form.commission_amount_manual && (
                  <span
                    className="absolute right-1 top-1/2 -translate-y-1/2 text-[8px] font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded px-1 py-[1px]"
                    title="Manually overridden — will not auto-recompute from Commission %"
                  >
                    MANUAL
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={form.commission_paid} onChange={e => update('commission_paid', e.target.checked)} className="rounded" data-testid="input-commission-paid" />
              <span className={`font-bold ${form.commission_paid ? 'text-emerald-700' : 'text-gray-500'}`}>Paid</span>
            </label>
            {form.commission_paid && (
              <Input type="date" value={form.commission_paid_date} onChange={e => update('commission_paid_date', e.target.value)} className="h-5 text-[10px] flex-1" data-testid="input-commission-paid-date" />
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="bg-white border border-gray-200 rounded-lg p-2.5 space-y-1.5">
          <p className="font-bold text-gray-700 flex items-center gap-1"><Calendar size={10} /> Notes</p>
          <textarea
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
            placeholder="Internal notes about this order..."
            className="w-full h-20 text-[10px] border border-gray-200 rounded px-2 py-1 resize-none"
            data-testid="input-fulfillment-notes"
          />
        </div>
      </div>

      {/* Save Button */}
      {dirty && (
        <div className="flex justify-end mt-3">
          <Button onClick={save} disabled={saving} size="sm" className="bg-purple-600 hover:bg-purple-700 gap-1 text-xs" data-testid="save-fulfillment-btn">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save Fulfillment
          </Button>
        </div>
      )}

      {/* Welcome Email for Digital Orders */}
      {hasDigitalItems && (
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3" data-testid="welcome-email-section">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-600" />
              <span className="text-xs font-bold text-blue-800">Welcome to MOSAIC Email</span>
              {(welcomeEmailSent || emailResult) && (
                <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                  <Check size={8} /> SENT
                </span>
              )}
            </div>
            {!welcomeEmailSent && !emailResult && (
              <Button
                size="sm"
                onClick={sendWelcomeEmail}
                disabled={sendingEmail}
                className="bg-[#002FA7] h-7 text-xs gap-1"
                data-testid="send-welcome-email-btn"
              >
                {sendingEmail ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                Send Welcome Email
              </Button>
            )}
          </div>
          <p className="text-[10px] text-blue-600">
            {welcomeEmailSent || emailResult
              ? `Welcome email sent${quote.welcome_email_sent_at ? ` on ${new Date(quote.welcome_email_sent_at).toLocaleDateString()}` : ''}. Admin credentials included.`
              : 'Send welcome email with admin startup credentials, Quick Start Guide, and digital license terms to the school contact.'}
          </p>
          {emailResult && (
            <div className="mt-2 bg-white border border-blue-200 rounded p-2 space-y-1 text-[10px]">
              {!emailResult.email_delivered && (
                <div className="bg-amber-50 border border-amber-300 rounded px-2 py-1 text-amber-700 font-bold text-[10px] mb-1">
                  Email delivery failed — credentials generated below. Verify SendGrid API key.
                </div>
              )}
              <p className="font-bold text-gray-700">Generated Admin Credentials:</p>
              <div className="font-mono bg-gray-50 px-2 py-1 rounded">
                <span className="text-gray-500">Email:</span> <span className="font-bold">{emailResult.admin_email}</span>
              </div>
              <div className="font-mono bg-amber-50 px-2 py-1 rounded border border-amber-200">
                <span className="text-gray-500">Password:</span> <span className="font-bold text-amber-700">{emailResult.admin_password}</span>
              </div>
              {emailResult.sent_to && emailResult.sent_to.length > 0 && (
                <div className="text-gray-500 text-[9px] mt-1">
                  {emailResult.sent_to.map((r, i) => (
                    <span key={i} className={`inline-flex items-center gap-0.5 mr-2 ${r.sent ? 'text-emerald-600' : 'text-red-500'}`}>
                      {r.sent ? <Check size={8} /> : '×'} {r.email}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-gray-400 text-[9px]">Save these credentials — they are also included in the email sent to the contact.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FulfillmentTracker;
